// Uploads an exercise-library video straight to S3 via a presigned POST, bypassing
// the API and its request timeout. A transcoded video lands on the final key; a
// deferred original stages at input/ for server-side MediaConvert.

import { postJson, postToS3, withRetry } from "./upload-video-transport";

/**
 * @param {{ videoTranscoded?: string }} body
 *   carries the client transcoder's outcome (set as hidden fields by useVideoTranscode).
 * @returns {Promise<{videoOriginal: string, uploaderId: string}>}
 *   the same shape the multipart other-exercise-video route returns.
 */
export default async function uploadOtherExerciseVideo({
  api,
  body,
  files,
  user,
}) {
  const file = files?.videoFile;

  // Authoritative signal from the client transcoder (useVideoTranscode): a
  // transcoded file is already conformant; anything else needs MediaConvert.
  const transcoded = body?.videoTranscoded === "true";

  const presign = await postJson(api, "other-exercise-video-presign", {
    userId: user._id,
    userEmail: user.email,
    fileName: file.name,
    contentType: file.type,
    transcoded,
  });

  await postToS3(presign, file);

  // Deferred original: the bytes are staged at input/; have MediaConvert build
  // the final mp4. Transcoded videos are already final — nothing more to do.
  if (!transcoded) {
    // Source size lets the server reproduce the bitrate the client would have used.
    // Absent when the transcoder could not read it. Safe to retry — MediaConvert
    // dedupes on the upload uuid, so a repeat cannot start a second job.
    await withRetry(
      () =>
        postJson(api, "other-exercise-video-finalize", {
          s3Key: presign.s3Key,
          width: body?.videoWidth || undefined,
          height: body?.videoHeight || undefined,
        }),
      "finalize"
    );
  }

  return {
    videoOriginal: presign.videoOriginal,
    uploaderId: presign.uploaderId,
  };
}
